import { Chart } from 'chart.js';
import {
  customXAxisLabelPlugin,
  customYAxisLabelPlugin,
} from '../../../../components/Charts/chartConfigs';

export const getAdImpressionsChartOptions = (labels: string[]) => {
  const shownMonths = new Set<string>();

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false },
    },
    layout: {
      padding: {
        left: 25,
      },
    },
    scales: {
      y: {
        grid: {
          display: true,
          color: '#AECBD0',
          tickWidth: 0,
        },
        border: {
          color: '#AECBD0',
          dash: [5, 5],
        },
        ticks: {
          color: '#989CA2',
          font: {
            family: 'Manrope',
            weight: 700,
          },
        },
      },
      x: {
        grid: { display: false },
        border: { color: '#AECBD0' },
        display: true,
        title: {
          text: 'Week',
          display: true,
        },
        ticks: {
          callback: (value: any, index: any) => {
            const label = labels[index];
            if (!label) return '';

            const currentYear = new Date().getFullYear().toString(); // e.g., "2025"
            const [year, month] = label.split('-');
            const monthKey = `${year}-${month}`;

            if (year !== currentYear) return ''; // 👈 Skip if not current year

            if (!shownMonths.has(monthKey)) {
              shownMonths.add(monthKey);
              return monthKey;
            }

            return '';
          },
          color: '#989CA2',
          font: {
            family: 'Manrope',
            weight: 700,
          },
        },
      },
    },
  };
};

export const getAdImpressionsChartPlugins = (clickedIndex: number | null) => [
  //   {
  //   id: 'chartShadowPlugin',
  //   beforeDraw: (chartInstance: any) => {
  //     let _stroke = chartInstance.ctx.stroke;
  //     chartInstance.ctx.stroke = function () {
  //       chartInstance.ctx.save();
  //       chartInstance.ctx.shadowColor = '#2F736E';
  //       chartInstance.ctx.shadowBlur = 5;
  //       chartInstance.ctx.shadowOffsetX = 2;
  //       chartInstance.ctx.shadowOffsetY = 1;
  //       _stroke.apply(this, arguments);
  //       chartInstance.ctx.restore();
  //     };
  //   }
  // },
  {
    id: 'verticalHoverLine',
    afterDatasetsDraw: (chart: Chart) => {
      const {
        ctx,
        chartArea: { top, bottom },
      } = chart;

      ctx.save();

      // Draw persistent line on click
      if (clickedIndex !== null) {
        const clickedMeta = chart.getDatasetMeta(0);
        const clickedPoint = clickedMeta.data[clickedIndex];
        if (clickedPoint) {
          ctx.beginPath();
          ctx.strokeStyle = '#3D4C56';
          ctx.setLineDash([5, 5]);
          ctx.moveTo(clickedPoint.x, top);
          ctx.lineTo(clickedPoint.x, bottom);
          ctx.stroke();
        }
      }

      // Draw current hover line
      chart.getDatasetMeta(0).data.forEach((datapoint: any) => {
        if (datapoint.active) {
          ctx.beginPath();
          ctx.strokeStyle = '#898C8D';
          ctx.setLineDash([5, 5]);
          ctx.moveTo(datapoint.x, top);
          ctx.lineTo(datapoint.x, bottom);
          ctx.stroke();
          ctx.setLineDash([]);
        }
      });

      ctx.restore();
    },
  },
  customXAxisLabelPlugin('WEEKS'),
  customYAxisLabelPlugin('AD IMPRESSIONS INDEX'),
];

export const getWeekCountUntilNow = () => {
  const currentYear = new Date().getFullYear();
  const getFirstWeekStartOfYear = (year: number): Date => {
    const jan1 = new Date(year, 0, 1);
    const day = jan1.getDay();
    const mondayOffset = day === 0 ? -6 : 1 - day;
    jan1.setDate(jan1.getDate() + mondayOffset);
    return jan1;
  };

  const startOfYear = getFirstWeekStartOfYear(currentYear);
  const today = new Date();
  const day = today.getDay();

  const endDate = new Date(today);
  if (day !== 0) {
    endDate.setDate(today.getDate() - day);
  }

  const diffTime = endDate.getTime() - startOfYear.getTime();
  const diffWeeks = Math.floor(diffTime / (7 * 24 * 60 * 60 * 1000)) + 1;

  return diffWeeks;
};

const monthNames = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const formatDateRange = (rangeStr: string) => {
  const [startStr, endStr] = rangeStr.split(' - ');
  const startDate = new Date(startStr);
  const endDate = new Date(endStr);

  const startDay = startDate.getDate();
  const startMonth = monthNames[startDate.getMonth()];
  const startYear = startDate.getFullYear();

  const endDay = endDate.getDate();
  const endMonth = monthNames[endDate.getMonth()];
  const endYear = endDate.getFullYear();

  const sameMonth = startMonth === endMonth;
  const sameYear = startYear === endYear;

  if (sameMonth && sameYear) {
    return `${startDay} - ${endDay} ${endMonth} ${endYear}`;
  } else if (!sameMonth && sameYear) {
    return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${endYear}`;
  } else {
    return `${startDay} ${startMonth} ${startYear} - ${endDay} ${endMonth} ${endYear}`;
  }
};

export const getFirstWeekStartOfYear = (year: number): Date => {
  const jan1 = new Date(year, 0, 1);
  const day = jan1.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  jan1.setDate(jan1.getDate() + mondayOffset);
  return jan1;
};

export const getLastCompletedWeekStart = (year: number): Date => {
  const startOfYear = getFirstWeekStartOfYear(year);

  const today = new Date();
  const day = today.getDay();

  const endDate = new Date(today);
  if (day !== 0) {
    endDate.setDate(today.getDate() - day);
  }

  const diffTime = endDate.getTime() - startOfYear.getTime();
  const diffWeeks = Math.floor(diffTime / (7 * 24 * 60 * 60 * 1000)) + 1;

  const lastWeekStartDate = new Date(startOfYear.getTime());
  lastWeekStartDate.setDate(startOfYear.getDate() + (diffWeeks - 1) * 7);

  return lastWeekStartDate;
};

export const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getWeekEnd = (weekStartStr: string): string => {
  const weekStart = new Date(weekStartStr); // e.g., "2024-12-30"
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekStart.getDate() + 6); // Add 6 days to get to Sunday
  return weekEnd.toISOString().split('T')[0]; // Return in YYYY-MM-DD format
};
